import { css } from '@emotion/react'
import { InterpolationPrimitive } from '@emotion/serialize'
import { Block } from 'components/Block'
import { Button } from 'components/button/Button'
import { useCreateDirectiveLogic } from 'components/directive/utils/directiveUtils'
import { Icon, IconName } from 'components/icon'
import { ContentfulImage } from 'components/Image'
import { HStack, Stack } from 'components/Stack'
import { Text, Title, Title_h3_default } from 'components/Typography'
import { SimpleFlowDirectiveType } from 'logic/directive/types'
import { Trans, useTranslation } from 'next-i18next'
import React, { ReactNode } from 'react'
import { colors } from 'utils/colors'
import { useContentfulImages } from 'utils/contentful/ContentfulImagesContext'
import { matchMap, numberRange, toLowerCase } from 'utils/general'
import { EstatePlanningPriceType } from 'utils/getInitialEstatePlanningPrice'
import { useWindowSize } from 'utils/hooks'
import { useEstatePlanPrice } from 'utils/promo'
import { BREAKPOINTS } from 'utils/styled'
import {
  SButtonWrapper,
  SIconWrapper,
  SLeftColumn,
  SPerkGrid,
  SRightColumn,
  STooltip,
  STrustWillPerksSection,
} from './TrustWillPerksSection.styled'

export type TrustWillPerksType = 'trust' | 'will'

export const PricingPlanPerk = ({
  icon,
  title,
  tooltipText,
  color,
  index,
  orientation,
  textCss,
}: {
  icon?: IconName
  title: ReactNode
  tooltipText: string
  color: string
  index: number
  orientation?: 'left' | 'right'
  textCss?: InterpolationPrimitive
}) => {
  const { isSmall, isLarge } = useWindowSize()

  const screenSizeBasedOrientation = !isSmall
    ? 'right'
    : isLarge
    ? 'left'
    : index < 3
    ? 'left'
    : 'right'

  return (
    <HStack space="1rem">
      <Icon name={icon} color={color} size="1.25rem" />
      <Text variant="body" color={colors.gray[100]} css={textCss}>
        {title}
      </Text>
      <SIconWrapper>
        <Icon name="Info" color={colors.gray[500]} size="1rem" />
        <STooltip orientation={orientation ?? screenSizeBasedOrientation}>{tooltipText}</STooltip>
      </SIconWrapper>
    </HStack>
  )
}

export const TrustWillPerksSection = ({
  directiveType,
  estatePlanningPrice,
  className,
}: {
  directiveType: SimpleFlowDirectiveType
  estatePlanningPrice: EstatePlanningPriceType
  className?: string
}) => {
  const { t: willT } = useTranslation('will_lp')
  const { t: trustT } = useTranslation('trust_lp')
  const { t: tEstate } = useTranslation('estate_planning_lp', {
    keyPrefix: 'estate_planning_lp.section_comparison',
  })
  const { t: tCommon } = useTranslation('common')
  const images = useContentfulImages()
  const { isMobile } = useWindowSize()

  const estatePlanningPriceForUser = useEstatePlanPrice(estatePlanningPrice)
  const { type: discountType } = estatePlanningPriceForUser

  const type = toLowerCase(directiveType)
  const analyticsPlace = matchMap(directiveType, {
    WILL: 'will_lp',
    TRUST: 'trust_lp',
  } as const)

  const { create } = useCreateDirectiveLogic(directiveType, {
    place: analyticsPlace,
  })

  const perkIcons = [
    'People',
    'DocumentReady',
    'DigitalVault',
    'CalendarRefresh',
    'Phone',
    'LifeStories',
  ] as const

  const isWill = type === 'will'
  const title = isWill
    ? willT('will_lp.section_perks.title', {
        context: isMobile ? 'mobile' : undefined,
      })
    : trustT('trust_lp.section_perks.title', {
        context: isMobile ? 'mobile' : undefined,
      })

  const description = isWill
    ? willT('will_lp.section_perks.description')
    : trustT('trust_lp.section_perks.description')

  const willPerks = numberRange(6).map((i) => ({
    icon: perkIcons[i - 1],
    title: (
      <Trans
        t={willT}
        i18nKey={`will_lp.section_perks.perks.perk_${i}`}
        components={{ b: <strong /> }}
      />
    ),
    tooltipText: willT(`will_lp.section_perks.perk_tooltips.perk_${i}`),
  }))
  const trustPerks = numberRange(6).map((i) => ({
    icon: perkIcons[i - 1],
    title: (
      <Trans
        t={trustT}
        i18nKey={`trust_lp.section_perks.perks.perk_${i}`}
        components={{ b: <strong /> }}
      />
    ),
    tooltipText: trustT(`trust_lp.section_perks.perk_tooltips.perk_${i}`),
  }))
  const perks = isWill ? willPerks : trustPerks

  const buttonText = isWill
    ? willT('will_lp.section_perks.button')
    : trustT('trust_lp.section_perks.button')

  const iconColor = type === 'will' ? colors.blue[500] : colors.green[200]
  const image = type === 'will' ? images.will_lp_perks_section : images.trust_lp_perks_section

  return (
    <Block className={className}>
      <STrustWillPerksSection>
        <SLeftColumn>
          <Stack space="1.5rem">
            <Title variant={Title_h3_default}>{title}</Title>
            {discountType !== 'recurring' && (
              <Text variant="bodyBold" color={colors.gray[100]} css={{ fontSize: '1.25rem' }}>
                {discountType === 'initial-no-discount' ? (
                  estatePlanningPriceForUser.undiscountedPriceText
                ) : discountType === 'initial-discount' ||
                  discountType === 'initial-free-subscription' ? (
                  <Trans
                    t={tEstate}
                    i18nKey="discounted_price"
                    values={{
                      initialPrice: estatePlanningPriceForUser.undiscountedPriceText,
                      discountedPrice:
                        discountType === 'initial-free-subscription'
                          ? tCommon('common.free')
                          : estatePlanningPriceForUser.discountedText,
                    }}
                    components={[
                      <Text
                        key={0}
                        variant="subtitleBold"
                        css={{ textDecoration: 'line-through' }}
                        color={colors.black}
                      />,
                      <Text key={1} variant="subtitleBold" color={colors.black} />,
                    ]}
                  />
                ) : null}
              </Text>
            )}

            <Text>{description}</Text>
            <SPerkGrid>
              {perks.map((perk, index) => (
                <PricingPlanPerk
                  key={perk.icon}
                  icon={perk.icon}
                  title={perk.title}
                  tooltipText={perk.tooltipText}
                  color={iconColor}
                  index={index}
                  textCss={css`
                    @media ${BREAKPOINTS.SM.min} {
                      flex: 0 1 60%;
                    }
                    @media ${BREAKPOINTS.LG.min} {
                      flex: unset;
                    }
                  `}
                />
              ))}
            </SPerkGrid>
            <SButtonWrapper>
              <Button
                onClick={create}
                type="button"
                size="large"
                customColor={!isWill ? colors.trust.primary : undefined}
                block={isMobile}
              >
                {buttonText}
              </Button>
            </SButtonWrapper>
          </Stack>
        </SLeftColumn>
        <SRightColumn>
          <ContentfulImage {...image} />
        </SRightColumn>
      </STrustWillPerksSection>
    </Block>
  )
}
